import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import PageSeo from "../components/PageSeo";
import { useApp } from "../context/AppContext";
import {
  MECHANIC_BRANDS,
  MECHANIC_SERVICES,
  getMyMechanics,
  submitMechanic,
  updateMechanic,
} from "../lib/mechanics";
import "./MechanicGarage.css";

const BRAND_OPTIONS = ["all", ...MECHANIC_BRANDS.filter((brand) => brand !== "all")];

function titleCase(value = "") {
  return String(value)
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function createForm(city = "", seed = {}) {
  return {
    name: seed.name || "",
    shop_name: seed.shop_name || "",
    city: seed.city || city || "",
    village: seed.village || "",
    district: seed.district || "",
    state: seed.state || "",
    phone_whatsapp: seed.phone_whatsapp || "",
    specialization: Array.isArray(seed.specialization) ? seed.specialization : [],
    services: Array.isArray(seed.services) ? seed.services : [],
    open_time: seed.open_time || "",
    close_time: seed.close_time || "",
    is_open_sunday: Boolean(seed.is_open_sunday),
    lat: seed.lat === null || seed.lat === undefined ? "" : String(seed.lat),
    lng: seed.lng === null || seed.lng === undefined ? "" : String(seed.lng),
    verification_method: seed.verification_method || "manual",
    verification_notes: seed.verification_notes || "",
  };
}

function toPayload(form, userId) {
  return {
    ...form,
    lat: form.lat ? Number(form.lat) : null,
    lng: form.lng ? Number(form.lng) : null,
    owner_user_id: userId,
  };
}

export default function MechanicGarage() {
  const { user } = useApp();
  const [garages, setGarages] = useState([]);
  const [selectedGarageId, setSelectedGarageId] = useState("");
  const [form, setForm] = useState(() => createForm(user?.city || ""));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const selectedGarage = useMemo(
    () => garages.find((garage) => garage.id === selectedGarageId) || null,
    [garages, selectedGarageId]
  );

  useEffect(() => {
    if (user?.city) {
      setForm((prev) => ({ ...prev, city: prev.city || user.city }));
    }
  }, [user?.city]);

  const loadGarages = async () => {
    if (user?.role !== "mechanic") {
      setGarages([]);
      return;
    }

    setLoading(true);
    const { data, error } = await getMyMechanics();
    if (error) {
      setToast(error.message || "Unable to load your garages");
      setGarages([]);
    } else {
      const list = data || [];
      setGarages(list);
      if (list.length) {
        const active = list.find((item) => item.id === selectedGarageId) || list[0];
        setSelectedGarageId(active.id);
        setForm(createForm(user?.city || "", active));
      } else {
        setSelectedGarageId("");
        setForm(createForm(user?.city || ""));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadGarages();
  }, [user?.role]);

  useEffect(() => {
    if (!selectedGarage) return;
    setForm(createForm(user?.city || "", selectedGarage));
  }, [selectedGarage, user?.city]);

  const toggleSelection = (field, value) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const normalized = String(value).toLowerCase();

      if (field === "specialization" && normalized === "all") {
        return current.includes("all")
          ? { ...prev, [field]: [] }
          : { ...prev, [field]: ["all"] };
      }

      if (field === "specialization" && current.includes("all")) {
        return { ...prev, [field]: [normalized] };
      }

      return current.includes(normalized)
        ? { ...prev, [field]: current.filter((item) => item !== normalized) }
        : { ...prev, [field]: [...current.filter((item) => item !== "all"), normalized] };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = toPayload(form, user?.id);
    const { data, error } = selectedGarageId
      ? await updateMechanic(selectedGarageId, payload)
      : await submitMechanic(payload);

    if (error) {
      setToast(error.message || "Unable to save garage");
    } else {
      setToast(selectedGarageId ? "Garage updated" : "Garage created");
      await loadGarages();
      if (!selectedGarageId && data?.id) {
        setSelectedGarageId(data.id);
      }
    }

    setSaving(false);
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!user) {
    return <Navigate to="/login" replace state={{ from: "/my-garage" }} />;
  }

  if (user.role !== "mechanic") {
    return (
      <div className="mechanic-garage-page">
        <PageSeo
          title="My Garage | MotoHub India"
          description="Manage your mechanic listing on MotoHub India."
          path="/my-garage"
          noindex
        />
        <div className="mechanic-garage-shell mechanic-garage-empty">
          <h1>My Garage is for mechanic accounts</h1>
          <p>Switch your account type to mechanic during signup, or ask support to update your profile role.</p>
          <Link to="/mechanic-finder" className="mechanic-garage-link">
            Go to Mechanic Finder
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mechanic-garage-page">
      <PageSeo
        title="My Garage | MotoHub India"
        description="Edit your own garage profile, manage bike specializations, and keep your mechanic listing up to date."
        path="/my-garage"
        keywords={["MotoHub India my garage", "mechanic dashboard", "edit garage profile"]}
      />

      <section className="mechanic-garage-shell mechanic-garage-hero">
        <div>
          <p className="mechanic-garage-kicker">Mechanic dashboard</p>
          <h1>My Garage</h1>
          <p className="mechanic-garage-copy">
            Keep your shop details, specialties, and WhatsApp contact updated. Riders see only verified listings, so this is your control room.
          </p>
        </div>
        <div className="mechanic-garage-hero-card">
          <strong>{garages.length}</strong>
          <span>Saved garage profiles</span>
        </div>
      </section>

      <section className="mechanic-garage-shell mechanic-garage-layout">
        <aside className="mechanic-garage-list">
          <div className="mechanic-garage-panel-head">
            <h2>Your garages</h2>
            <span>{loading ? "Loading..." : `${garages.length} entries`}</span>
          </div>
          {garages.length ? (
            garages.map((garage) => (
              <button
                key={garage.id}
                type="button"
                className={`mechanic-garage-item ${selectedGarageId === garage.id ? "is-active" : ""}`}
                onClick={() => {
                  setSelectedGarageId(garage.id);
                  setForm(createForm(user?.city || "", garage));
                }}
              >
                <strong>{garage.shop_name}</strong>
                <span>{garage.city}, {garage.district}</span>
                <small>{garage.is_verified ? "Verified" : "Pending review"}</small>
              </button>
            ))
          ) : (
            <div className="mechanic-garage-empty-state">
              <p>No garage profile yet.</p>
              <span>Create one below so riders can find your workshop.</span>
            </div>
          )}
        </aside>

        <form className="mechanic-garage-form" onSubmit={handleSave}>
          <div className="mechanic-garage-panel-head">
            <h2>{selectedGarageId ? "Edit garage" : "Create garage"}</h2>
            <span>Owner only</span>
          </div>

          <div className="mechanic-garage-grid">
            <label>
              <span>Mechanic name</span>
              <input required value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
            </label>
            <label>
              <span>Shop name</span>
              <input required value={form.shop_name} onChange={(event) => setForm((prev) => ({ ...prev, shop_name: event.target.value }))} />
            </label>
            <label>
              <span>City</span>
              <input required value={form.city} onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))} />
            </label>
            <label>
              <span>Village</span>
              <input required value={form.village} onChange={(event) => setForm((prev) => ({ ...prev, village: event.target.value }))} />
            </label>
            <label>
              <span>District</span>
              <input required value={form.district} onChange={(event) => setForm((prev) => ({ ...prev, district: event.target.value }))} />
            </label>
            <label>
              <span>State</span>
              <input required value={form.state} onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))} />
            </label>
            <label>
              <span>WhatsApp number</span>
              <input required value={form.phone_whatsapp} onChange={(event) => setForm((prev) => ({ ...prev, phone_whatsapp: event.target.value }))} placeholder="+91 98765 43210" />
            </label>
            <label>
              <span>Open time</span>
              <input type="time" value={form.open_time} onChange={(event) => setForm((prev) => ({ ...prev, open_time: event.target.value }))} />
            </label>
            <label>
              <span>Close time</span>
              <input type="time" value={form.close_time} onChange={(event) => setForm((prev) => ({ ...prev, close_time: event.target.value }))} />
            </label>
            <label className="mechanic-garage-check">
              <span>Open on Sunday</span>
              <input
                type="checkbox"
                checked={form.is_open_sunday}
                onChange={(event) => setForm((prev) => ({ ...prev, is_open_sunday: event.target.checked }))}
              />
            </label>
            <label>
              <span>Location note</span>
              <input
                value={form.verification_notes}
                onChange={(event) => setForm((prev) => ({ ...prev, verification_notes: event.target.value }))}
                placeholder="Map link or OTP reference"
              />
            </label>
            <label>
              <span>Verification source</span>
              <select value={form.verification_method} onChange={(event) => setForm((prev) => ({ ...prev, verification_method: event.target.value }))}>
                <option value="manual">Manual review</option>
                <option value="mobile_otp">Mobile OTP</option>
                <option value="map_location">Map location share</option>
              </select>
            </label>
          </div>

          <div className="mechanic-garage-tags">
            <span>Specialization</span>
            <div className="mechanic-garage-chip-row">
              {BRAND_OPTIONS.map((brand) => (
                <button
                  key={`garage-brand-${brand}`}
                  type="button"
                  className={`mechanic-garage-chip ${form.specialization.includes(brand) ? "is-active" : ""}`}
                  onClick={() => toggleSelection("specialization", brand)}
                >
                  {brand === "all" ? "All Bikes" : titleCase(brand)}
                </button>
              ))}
            </div>
          </div>

          <div className="mechanic-garage-tags">
            <span>Services</span>
            <div className="mechanic-garage-chip-row">
              {MECHANIC_SERVICES.map((service) => (
                <button
                  key={`garage-service-${service}`}
                  type="button"
                  className={`mechanic-garage-chip ${form.services.includes(service) ? "is-active" : ""}`}
                  onClick={() => toggleSelection("services", service)}
                >
                  {titleCase(service)}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" className="mechanic-garage-save" disabled={saving}>
            {saving ? "Saving..." : selectedGarageId ? "Save Changes" : "Create Garage"}
          </button>
        </form>
      </section>

      {toast ? <div className="mechanic-garage-toast">{toast}</div> : null}
    </div>
  );
}
